import { useState, useEffect, useMemo, useCallback } from 'react'
import dayjs from 'dayjs'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Tooltip from '@mui/material/Tooltip'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import api from 'services/api'
import { PERSONAS } from './cohorts'
import { personaHours } from './micTime'

const DAY_MINUTES = 24 * 60
const POLL_MS = 5 * 60 * 1000
// The prior-week starts move a day at a time, and their 7 subqueries are
// the most expensive reads this page issues — refresh them hourly. Their
// day boundaries lag local midnight by up to an hour as a result, which
// shifts the 7-day totals imperceptibly.
const PRIOR_POLL_MS = 60 * 60 * 1000

const elapsedTodayMinutes = () =>
  Math.max(5, dayjs().diff(dayjs().startOf('day'), 'minute'))

// Session starts = sum of positive tick-to-tick jumps in each cohort's
// concurrency, accumulated by a subquery at the metric's native 30s
// cadence. idelta over 90s tolerates one missed scrape.
const startsQuery = (window, offset) =>
  'sum by (user_type, account_age, usage_tier) ' +
  '(sum_over_time((clamp_min(idelta(reclip_recording_mic_on_users[90s]), 0))' +
  `[${window}:30s]${offset ? ` offset ${offset}` : ''}))`

const zeroCounts = () => {
  const counts = {}
  PERSONAS.forEach(({ key }) => {
    counts[key] = 0
  })
  return counts
}

const toPersonaCounts = (response) => {
  const counts = zeroCounts()
  const result = response?.data?.result || []
  result.forEach(({ metric, value }) => {
    const persona = PERSONAS.find(({ match }) => match(metric || {}))
    if (persona) counts[persona.key] += parseFloat(value?.[1]) || 0
  })
  return counts
}

const addCounts = (a, b) => {
  const out = { ...a }
  PERSONAS.forEach(({ key }) => {
    out[key] += b[key]
  })
  return out
}

// Sessions already running at a day boundary produce no start jump inside
// that day; count them via the concurrency level at the boundary.
const personaLevelAt = (history, ts) => {
  const counts = zeroCounts()
  history.series.forEach((entry) => {
    const persona = PERSONAS.find(({ match }) => match(entry.labels || {}))
    if (!persona) return
    let level = 0
    for (const [pointTs, value] of entry.points) {
      if (pointTs > ts + history.step_seconds) break
      level = value
    }
    counts[persona.key] += level
  })
  return counts
}

const fmtSession = (hours) => {
  if (hours == null || !isFinite(hours)) return '—'
  const minutes = hours * 60
  if (minutes < 1) return `${Math.max(1, Math.round(minutes * 60))}s`
  if (minutes < 90) return `${Math.round(minutes)}m`
  return `${hours.toFixed(1)}h`
}

const SessionAverages = ({ day, week }) => {
  const [todayStarts, setTodayStarts] = useState(null)
  const [priorStarts, setPriorStarts] = useState(null)
  const [error, setError] = useState(null)

  const loadToday = useCallback(async () => {
    try {
      const response = await api.recordingMetrics.promql(
        startsQuery(`${elapsedTodayMinutes()}m`)
      )
      setTodayStarts(toPersonaCounts(response))
      setError(null)
    } catch (err) {
      setError(err.message)
    }
  }, [])

  const loadPrior = useCallback(async () => {
    try {
      const elapsedMin = elapsedTodayMinutes()
      const prior = await Promise.all(
        Array.from({ length: 7 }, (unused, index) =>
          api.recordingMetrics.promql(
            startsQuery('24h', `${elapsedMin + DAY_MINUTES * index}m`)
          )
        )
      )
      setPriorStarts(prior.map(toPersonaCounts).reduce(addCounts))
      setError(null)
    } catch (err) {
      setError(err.message)
    }
  }, [])

  useEffect(() => {
    loadToday()
    const interval = setInterval(loadToday, POLL_MS)
    return () => clearInterval(interval)
  }, [loadToday])

  useEffect(() => {
    loadPrior()
    const interval = setInterval(loadPrior, PRIOR_POLL_MS)
    return () => clearInterval(interval)
  }, [loadPrior])

  const stats = useMemo(() => {
    if (!day || !todayStarts || day.series.length === 0) return null

    const todayHours = personaHours(day)
    const carriedToday = personaLevelAt(day, day.start)

    let priorHours = null
    let priorCarried = null
    if (priorStarts && week && week.series.length > 0) {
      priorHours = personaHours(week, 0, day.start)
      priorCarried = zeroCounts()
      for (let back = 1; back <= 7; back += 1) {
        const boundary = day.start - back * 86400
        if (boundary < week.start) break
        priorCarried = addCounts(priorCarried, personaLevelAt(week, boundary))
      }
    }

    const rows = PERSONAS.map(({ key, color }) => {
      const sessions = todayStarts[key] + carriedToday[key]
      const priorSessions = priorHours
        ? priorStarts[key] + priorCarried[key]
        : 0
      return {
        key,
        color,
        today: sessions > 0 ? todayHours[key] / sessions : null,
        typical: priorSessions > 0 ? priorHours[key] / priorSessions : null,
      }
    })

    const total = (counts) =>
      PERSONAS.reduce((sum, { key }) => sum + counts[key], 0)
    const todaySessions = total(todayStarts) + total(carriedToday)
    const priorSessions = priorHours
      ? total(priorStarts) + total(priorCarried)
      : 0
    return {
      rows,
      today: todaySessions > 0 ? total(todayHours) / todaySessions : null,
      typical: priorSessions > 0 ? total(priorHours) / priorSessions : null,
    }
  }, [day, week, todayStarts, priorStarts])

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Session length
        </Typography>
        <Tooltip
          title="Per persona: today / typical (prior 7 days). Sessions are estimated from jumps in the concurrency metric, so back-to-back restarts read as one session."
          placement="right"
        >
          <InfoOutlinedIcon
            sx={{ fontSize: 16, color: 'text.secondary', cursor: 'default' }}
          />
        </Tooltip>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 1.5 }}
      >
        ≈ average continuous mic-on stretch
      </Typography>

      {stats ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h4" fontWeight={800} lineHeight={1}>
              {fmtSession(stats.today)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              today
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            typical day ≈ {fmtSession(stats.typical)}
          </Typography>

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            {stats.rows.map(({ key, color, today, typical }) => (
              <Box
                key={key}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ flex: 1, fontWeight: 600, letterSpacing: 0.5 }}
                >
                  {key}
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {fmtSession(today)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  / {fmtSession(typical)}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      ) : error ? (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {error && stats && (
        <Typography variant="caption" color="error" display="block">
          Last refresh failed: {error}
        </Typography>
      )}
    </>
  )
}

export default SessionAverages
